import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getBoards,
  getBoardById,
  createBoard,
  updateBoard,
  deleteBoard,
  deleteBoardPermanently,
  getBoardMembers,
  addBoardMember,
  removeBoardMember,
  getBoardLists,
  createBoardList,
  updateBoardList,
  reorderBoardLists,
  deleteBoardList,
  getBoardCards,
  getCardById,
  createCard,
  updateCard,
  moveCard,
  deleteCard,
  uploadCardAttachments,
  deleteCardAttachment,
  getCardComments,
  addCardComment,
  getCardChecklists,
  createChecklist,
  deleteChecklist,
  createChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  getCardActivities,
  getBoardLabels,
  createBoardLabel,
  updateBoardLabel,
  deleteBoardLabel,
  type CreateBoardPayload,
  type UpdateBoardPayload,
  type CreateListPayload,
  type UpdateListPayload,
  type BoardData,
  type BoardMemberData,
  type BoardListData,
  type CreateCardPayload,
  type UpdateCardPayload,
  type MoveCardPayload,
  type CardData,
  type CardAttachmentData,
  type CommentData,
  type ChecklistData,
  type ChecklistItemData,
  type LabelData,
} from '@/api/services/boards.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import {
  addChecklistItem,
  countChecklistLeaves,
  removeChecklistItem,
  toggleChecklistItem,
} from '../utils/checklistTree'
import { useAuthStore } from '@/stores/useAuthStore'

export const boardsKeys = {
  all: ['boards'] as const,
  list: (archived = false) => ['boards', 'list', { archived }] as const,
  detail: (id: string) => ['boards', 'detail', id] as const,
  members: (boardId: string) => ['boards', boardId, 'members'] as const,
  lists: (boardId: string, archived?: boolean) => archived === undefined
    ? ['boards', boardId, 'lists'] as const
    : ['boards', boardId, 'lists', { archived }] as const,
  cards: (boardId: string) => ['boards', boardId, 'cards'] as const,
  archivedCards: (boardId: string) => ['boards', boardId, 'cards', { archived: true }] as const,
  cardDetail: (boardId: string, cardId: string) => ['boards', boardId, 'cards', cardId] as const,
  comments: (boardId: string, cardId: string) => ['boards', boardId, 'cards', cardId, 'comments'] as const,
  checklists: (boardId: string, cardId: string) => ['boards', boardId, 'cards', cardId, 'checklists'] as const,
  activities: (boardId: string, cardId: string) => ['boards', boardId, 'cards', cardId, 'activities'] as const,
  labels: (boardId: string) => ['boards', boardId, 'labels'] as const,
}

const optimisticId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

/** Older card endpoints can return ObjectId arrays. Keep the hydrated values
 * already visible in the UI so an otherwise unrelated update never makes
 * members or labels disappear. */
const mergeCardServerResponse = (current: CardData | undefined, serverCard: CardData): CardData => ({
  ...current,
  ...serverCard,
  ...(current?.assignees ? { assignees: current.assignees } : {}),
  ...(current?.labels ? { labels: current.labels } : {}),
})

// ─── Boards ────────────────────────────────────────

export function useBoards(archived = false) {
  return useQuery({
    queryKey: boardsKeys.list(archived),
    queryFn: () => getBoards(archived),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useBoard(id: string | null) {
  return useQuery({
    queryKey: boardsKeys.detail(id ?? ''),
    queryFn: () => getBoardById(id!),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  })
}

export function useBoardMembers(boardId: string | null) {
  return useQuery({
    queryKey: boardsKeys.members(boardId ?? ''),
    queryFn: () => getBoardMembers(boardId!),
    enabled: !!boardId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useAddBoardMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, user }: { boardId: string; user: BoardMemberData['userId'] }) =>
      addBoardMember(boardId, user._id),
    onMutate: async (variables) => {
      const queryKey = boardsKeys.members(variables.boardId)
      await qc.cancelQueries({ queryKey })
      const previousMembers = qc.getQueryData<BoardMemberData[]>(queryKey)
      const temporaryMember: BoardMemberData = {
        _id: optimisticId('board-member'), boardId: variables.boardId, userId: variables.user, role: 'member',
      }
      qc.setQueryData<BoardMemberData[]>(queryKey, (current) => [...(current ?? []), temporaryMember])
      return { previousMembers, temporaryMemberId: temporaryMember._id }
    },
    onSuccess: (member, variables, context) => {
      qc.setQueryData<BoardMemberData[]>(boardsKeys.members(variables.boardId), (current) =>
        current?.map((entry) => entry._id === context?.temporaryMemberId
          // Keep the selected user's populated profile even if an older API
          // response returns only the ObjectId.
          ? { ...member, userId: variables.user }
          : entry),
      )
    },
    onError: (error, variables, context) => {
      qc.setQueryData(boardsKeys.members(variables.boardId), context?.previousMembers)
      toast.error(getErrorMessage(error))
    },
  })
}

export function useRemoveBoardMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, member }: { boardId: string; member: BoardMemberData }) =>
      removeBoardMember(boardId, member.userId._id),
    onMutate: async ({ boardId, member }) => {
      const queryKey = boardsKeys.members(boardId)
      await qc.cancelQueries({ queryKey })
      const previousMembers = qc.getQueryData<BoardMemberData[]>(queryKey)
      qc.setQueryData<BoardMemberData[]>(queryKey, (current) => current?.filter((entry) => entry._id !== member._id))
      return { previousMembers }
    },
    onError: (error, variables, context) => {
      qc.setQueryData(boardsKeys.members(variables.boardId), context?.previousMembers)
      toast.error(getErrorMessage(error))
    },
  })
}

export function useCreateBoard() {
  const qc = useQueryClient()
  const currentUser = useAuthStore((state) => state.user)
  return useMutation({
    mutationFn: (payload: CreateBoardPayload) => createBoard(payload),
    onMutate: async (payload) => {
      const queryKey = boardsKeys.list(false)
      await qc.cancelQueries({ queryKey })
      const previousBoards = qc.getQueryData<BoardData[]>(queryKey)
      const now = new Date().toISOString()
      const creatorNameParts = currentUser?.name?.trim().split(/\s+/) ?? []
      const optimisticCreator = currentUser
        ? {
            _id: currentUser.id,
            firstName: currentUser.firstName ?? creatorNameParts[0] ?? '',
            lastName: currentUser.lastName ?? creatorNameParts.slice(1).join(' '),
            email: currentUser.email,
            avatar: currentUser.avatar ?? undefined,
          }
        : ''
      const temporaryBoard: BoardData = {
        _id: optimisticId('board'), organizationId: '', title: payload.title, description: payload.description,
        type: payload.type ?? 'team', visibility: payload.visibility ?? 'private', entityType: payload.entityType,
        entityId: payload.entityId, background: payload.background, createdBy: optimisticCreator, isTemplate: false,
        isArchived: false, isClosed: false, createdAt: now, updatedAt: now,
      }
      qc.setQueryData<BoardData[]>(queryKey, (current) => [temporaryBoard, ...(current ?? [])])
      return { previousBoards, temporaryBoardId: temporaryBoard._id }
    },
    onSuccess: (board, _payload, context) => {
      qc.setQueryData<BoardData[]>(boardsKeys.list(false), (current) =>
        current?.map((entry) => entry._id === context?.temporaryBoardId
          ? {
              ...board,
              // A list refetch always returns this populated. Preserve the
              // already-known real creator if an older create response only
              // returns the creator's ObjectId.
              createdBy: typeof board.createdBy === 'object' ? board.createdBy : entry.createdBy,
            }
          : entry),
      )
      toast.success('Board created')
    },
    onError: (e, _payload, context) => {
      qc.setQueryData(boardsKeys.list(false), context?.previousBoards)
      toast.error(getErrorMessage(e))
    },
  })
}

export function useUpdateBoard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBoardPayload; silent?: boolean }) => updateBoard(id, payload),
    onMutate: async (variables) => {
      await Promise.all([
        qc.cancelQueries({ queryKey: boardsKeys.detail(variables.id) }),
        qc.cancelQueries({ queryKey: ['boards', 'list'] }),
      ])

      const previousBoard = qc.getQueryData<BoardData>(boardsKeys.detail(variables.id))
      const previousBoardLists = qc.getQueriesData<BoardData[]>({ queryKey: ['boards', 'list'] })
      const optimisticBoard = (current: BoardData | undefined) => current
        ? { ...current, ...variables.payload, updatedAt: new Date().toISOString() }
        : current

      qc.setQueryData<BoardData>(boardsKeys.detail(variables.id), optimisticBoard)
      if (typeof variables.payload.isArchived === 'boolean') {
        const sourceKey = boardsKeys.list(!variables.payload.isArchived)
        const targetKey = boardsKeys.list(variables.payload.isArchived)
        const sourceBoard = qc.getQueryData<BoardData[]>(sourceKey)?.find((board) => board._id === variables.id)
        const targetBoard = sourceBoard ?? qc.getQueryData<BoardData[]>(targetKey)?.find((board) => board._id === variables.id)
        const optimistic = targetBoard
          ? { ...targetBoard, ...variables.payload, updatedAt: new Date().toISOString() }
          : undefined
        qc.setQueryData<BoardData[]>(sourceKey, (current) => current?.filter((board) => board._id !== variables.id))
        if (optimistic) {
          qc.setQueryData<BoardData[]>(targetKey, (current) => [
            optimistic,
            ...(current ?? []).filter((board) => board._id !== variables.id),
          ])
        }
      } else {
        previousBoardLists.forEach(([key]) => {
          qc.setQueryData<BoardData[]>(key, (current) => current?.map((board) =>
            board._id === variables.id
              ? { ...board, ...variables.payload, updatedAt: new Date().toISOString() }
              : board,
          ))
        })
      }

      return { previousBoard, previousBoardLists }
    },
    onSuccess: (updatedBoard, v) => {
      qc.setQueryData<BoardData>(boardsKeys.detail(v.id), (current) => current
        ? { ...current, ...updatedBoard, currentUserRole: updatedBoard.currentUserRole ?? current.currentUserRole }
        : updatedBoard,
      )
      if (typeof v.payload.isArchived === 'boolean') {
        const sourceKey = boardsKeys.list(!v.payload.isArchived)
        const targetKey = boardsKeys.list(v.payload.isArchived)
        qc.setQueryData<BoardData[]>(sourceKey, (current) => current?.filter((board) => board._id !== v.id))
        qc.setQueryData<BoardData[]>(targetKey, (current) => [
          updatedBoard,
          ...(current ?? []).filter((board) => board._id !== v.id),
        ])
      } else {
        qc.setQueriesData<BoardData[]>({ queryKey: ['boards', 'list'] }, (current) => current?.map((board) =>
          board._id === v.id ? { ...board, ...updatedBoard } : board,
        ))
      }
      if (!v.silent) toast.success('Board updated')
    },
    onError: (e, variables, context) => {
      qc.setQueryData(boardsKeys.detail(variables.id), context?.previousBoard)
      context?.previousBoardLists.forEach(([key, data]) => qc.setQueryData(key, data))
      toast.error(getErrorMessage(e))
    },
  })
}

export function useDeleteBoard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteBoard(id),
    onMutate: async (id) => {
      await Promise.all([qc.cancelQueries({ queryKey: boardsKeys.list(false) }), qc.cancelQueries({ queryKey: boardsKeys.list(true) })])
      const previousActiveBoards = qc.getQueryData<BoardData[]>(boardsKeys.list(false))
      const previousArchivedBoards = qc.getQueryData<BoardData[]>(boardsKeys.list(true))
      const board = previousActiveBoards?.find((entry) => entry._id === id)
      qc.setQueryData<BoardData[]>(boardsKeys.list(false), (current) => current?.filter((entry) => entry._id !== id))
      if (board) qc.setQueryData<BoardData[]>(boardsKeys.list(true), (current) => [{ ...board, isArchived: true }, ...(current ?? [])])
      return { previousActiveBoards, previousArchivedBoards }
    },
    onSuccess: () => {
      toast.success('Board archived')
    },
    onError: (e, _id, context) => {
      qc.setQueryData(boardsKeys.list(false), context?.previousActiveBoards)
      qc.setQueryData(boardsKeys.list(true), context?.previousArchivedBoards)
      toast.error(getErrorMessage(e))
    },
  })
}

export function useDeleteBoardPermanently() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteBoardPermanently(id),
    onMutate: async (id) => {
      await Promise.all([qc.cancelQueries({ queryKey: boardsKeys.list(false) }), qc.cancelQueries({ queryKey: boardsKeys.list(true) })])
      const previousActiveBoards = qc.getQueryData<BoardData[]>(boardsKeys.list(false))
      const previousArchivedBoards = qc.getQueryData<BoardData[]>(boardsKeys.list(true))
      qc.setQueryData<BoardData[]>(boardsKeys.list(false), (current) => current?.filter((entry) => entry._id !== id))
      qc.setQueryData<BoardData[]>(boardsKeys.list(true), (current) => current?.filter((entry) => entry._id !== id))
      return { previousActiveBoards, previousArchivedBoards }
    },
    onSuccess: () => {
      toast.success('Board permanently deleted')
    },
    onError: (e, _id, context) => {
      qc.setQueryData(boardsKeys.list(false), context?.previousActiveBoards)
      qc.setQueryData(boardsKeys.list(true), context?.previousArchivedBoards)
      toast.error(getErrorMessage(e))
    },
  })
}

// ─── Lists ─────────────────────────────────────────

export function useBoardLists(boardId: string | null, archived = false, enabled = true) {
  return useQuery({
    queryKey: boardsKeys.lists(boardId ?? '', archived),
    queryFn: () => getBoardLists(boardId!, archived),
    enabled: !!boardId && enabled,
    staleTime: 60 * 1000,
  })
}

export function useCreateList() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, payload }: { boardId: string; payload: CreateListPayload }) =>
      createBoardList(boardId, payload),
    onMutate: async (v) => {
      const queryKey = boardsKeys.lists(v.boardId, false)
      await qc.cancelQueries({ queryKey })
      const previousLists = qc.getQueryData<BoardListData[]>(queryKey)
      const tempId = optimisticId('list')
      const position = v.payload.position ?? Math.max(0, ...(previousLists ?? []).map((list) => list.position)) + 1000
      const optimisticList: BoardListData = {
        _id: tempId, organizationId: '', boardId: v.boardId, title: v.payload.title,
        position, color: v.payload.color, wipLimit: v.payload.wipLimit ?? 0,
        isDoneColumn: v.payload.isDoneColumn ?? false, isArchived: false,
      }
      qc.setQueryData<BoardListData[]>(queryKey, (current) => [...(current ?? []), optimisticList])
      return { previousLists, tempId }
    },
    onSuccess: (createdList, v, context) => {
      qc.setQueryData<BoardListData[]>(boardsKeys.lists(v.boardId, false), (current) =>
        current?.map((list) => list._id === context?.tempId ? createdList : list),
      )
      toast.success('Column added')
    },
    onError: (e, v, context) => {
      qc.setQueryData(boardsKeys.lists(v.boardId, false), context?.previousLists)
      toast.error(getErrorMessage(e))
    },
  })
}

export function useUpdateList() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, listId, payload }: { boardId: string; listId: string; payload: UpdateListPayload }) =>
      updateBoardList(boardId, listId, payload),
    onMutate: async (v) => {
      const activeKey = boardsKeys.lists(v.boardId, false)
      const archivedKey = boardsKeys.lists(v.boardId, true)
      await Promise.all([qc.cancelQueries({ queryKey: activeKey }), qc.cancelQueries({ queryKey: archivedKey })])
      const previousActive = qc.getQueryData<BoardListData[]>(activeKey)
      const previousArchived = qc.getQueryData<BoardListData[]>(archivedKey)
      const update = (list: BoardListData) => ({ ...list, ...v.payload })

      if (v.payload.isArchived === true) {
        const target = previousActive?.find((list) => list._id === v.listId)
        qc.setQueryData<BoardListData[]>(activeKey, (current) => current?.filter((list) => list._id !== v.listId))
        if (target) qc.setQueryData<BoardListData[]>(archivedKey, (current) => [...(current ?? []), update(target)])
      } else if (v.payload.isArchived === false) {
        const target = previousArchived?.find((list) => list._id === v.listId)
        qc.setQueryData<BoardListData[]>(archivedKey, (current) => current?.filter((list) => list._id !== v.listId))
        if (target) qc.setQueryData<BoardListData[]>(activeKey, (current) => [...(current ?? []), update(target)].sort((a, b) => a.position - b.position))
      } else {
        qc.setQueryData<BoardListData[]>(activeKey, (current) => current?.map((list) => list._id === v.listId ? update(list) : list))
        qc.setQueryData<BoardListData[]>(archivedKey, (current) => current?.map((list) => list._id === v.listId ? update(list) : list))
      }
      return { previousActive, previousArchived }
    },
    onSuccess: (updatedList, v) => {
      const activeKey = boardsKeys.lists(v.boardId, false)
      const archivedKey = boardsKeys.lists(v.boardId, true)
      qc.setQueryData<BoardListData[]>(activeKey, (current) => current?.map((list) => list._id === v.listId ? updatedList : list))
      qc.setQueryData<BoardListData[]>(archivedKey, (current) => current?.map((list) => list._id === v.listId ? updatedList : list))
    },
    onError: (e, v, context) => {
      qc.setQueryData(boardsKeys.lists(v.boardId, false), context?.previousActive)
      qc.setQueryData(boardsKeys.lists(v.boardId, true), context?.previousArchived)
      toast.error(getErrorMessage(e))
    },
  })
}

export function useReorderLists() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, listIds }: { boardId: string; listIds: string[] }) =>
      reorderBoardLists(boardId, listIds),
    onMutate: async (v) => {
      const queryKey = boardsKeys.lists(v.boardId, false)
      await qc.cancelQueries({ queryKey })
      const previousLists = qc.getQueryData<BoardListData[]>(queryKey)
      qc.setQueryData<BoardListData[]>(queryKey, (current) => {
        if (!current) return current
        const byId = new Map(current.map((list) => [list._id, list]))
        return v.listIds.map((id, index) => ({ ...byId.get(id)!, position: (index + 1) * 1000 })).filter(Boolean)
      })
      return { previousLists }
    },
    onError: (e, v, context) => {
      qc.setQueryData(boardsKeys.lists(v.boardId, false), context?.previousLists)
      toast.error(getErrorMessage(e))
    },
  })
}

export function useDeleteList() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, listId }: { boardId: string; listId: string }) =>
      deleteBoardList(boardId, listId),
    onMutate: async (v) => {
      const activeKey = boardsKeys.lists(v.boardId, false)
      const archivedKey = boardsKeys.lists(v.boardId, true)
      await Promise.all([qc.cancelQueries({ queryKey: activeKey }), qc.cancelQueries({ queryKey: archivedKey })])
      const previousActive = qc.getQueryData<BoardListData[]>(activeKey)
      const previousArchived = qc.getQueryData<BoardListData[]>(archivedKey)
      const target = previousActive?.find((list) => list._id === v.listId)
      qc.setQueryData<BoardListData[]>(activeKey, (current) => current?.filter((list) => list._id !== v.listId))
      if (target) qc.setQueryData<BoardListData[]>(archivedKey, (current) => [...(current ?? []), { ...target, isArchived: true }])
      return { previousActive, previousArchived }
    },
    onSuccess: () => {
      toast.success('Column archived')
    },
    onError: (e, v, context) => {
      qc.setQueryData(boardsKeys.lists(v.boardId, false), context?.previousActive)
      qc.setQueryData(boardsKeys.lists(v.boardId, true), context?.previousArchived)
      toast.error(getErrorMessage(e))
    },
  })
}

// ─── Cards ─────────────────────────────────────────

export function useBoardCards(boardId: string | null) {
  return useQuery({
    queryKey: boardsKeys.cards(boardId ?? ''),
    queryFn: () => getBoardCards(boardId!),
    enabled: !!boardId,
    staleTime: 30 * 1000,
  })
}

export function useArchivedBoardCards(boardId: string | null, enabled = true) {
  return useQuery({
    queryKey: boardsKeys.archivedCards(boardId ?? ''),
    queryFn: () => getBoardCards(boardId!, true),
    enabled: !!boardId && enabled,
    staleTime: 60 * 1000,
  })
}

export function useCardDetail(boardId: string | null, cardId: string | null) {
  return useQuery({
    queryKey: boardsKeys.cardDetail(boardId ?? '', cardId ?? ''),
    queryFn: () => getCardById(boardId!, cardId!),
    enabled: !!boardId && !!cardId,
    staleTime: 30 * 1000,
  })
}

export function useCreateCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, payload }: { boardId: string; payload: CreateCardPayload }) =>
      createCard(boardId, payload),
    onMutate: async (v) => {
      const queryKey = boardsKeys.cards(v.boardId)
      await qc.cancelQueries({ queryKey })
      const previousCards = qc.getQueryData<CardData[]>(queryKey)
      const tempId = optimisticId('card')
      const now = new Date().toISOString()
      const position = v.payload.position ?? Math.max(0, ...(previousCards ?? []).filter((card) => card.listId === v.payload.listId).map((card) => card.position)) + 1000
      const optimisticCard: CardData = {
        _id: tempId, organizationId: '', boardId: v.boardId, listId: v.payload.listId,
        title: v.payload.title, description: v.payload.description ?? '', position,
        priority: v.payload.priority ?? 'none', entityType: v.payload.entityType, entityId: v.payload.entityId,
        assignees: [], labels: [], createdBy: 'pending',
        isArchived: false, createdAt: now, updatedAt: now,
      }
      qc.setQueryData<CardData[]>(queryKey, (current) => [...(current ?? []), optimisticCard])
      return { previousCards, tempId }
    },
    onSuccess: (createdCard, v, context) => {
      qc.setQueryData<CardData[]>(boardsKeys.cards(v.boardId), (current) =>
        current?.map((card) => card._id === context?.tempId ? createdCard : card),
      )
    },
    onError: (e, v, context) => {
      qc.setQueryData(boardsKeys.cards(v.boardId), context?.previousCards)
      toast.error(getErrorMessage(e))
    },
  })
}

/**
 * Optimistic update: instantly patches the card in the cache so the UI
 * reflects changes without waiting for the API round-trip.
 */
export function useUpdateCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, cardId, payload }: { boardId: string; cardId: string; payload: UpdateCardPayload; optimisticAssignees?: CardData['assignees']; optimisticLabels?: CardData['labels'] }) =>
      updateCard(boardId, cardId, payload),
    onMutate: async (v) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await qc.cancelQueries({ queryKey: boardsKeys.cards(v.boardId) })
      await qc.cancelQueries({ queryKey: boardsKeys.archivedCards(v.boardId) })
      await qc.cancelQueries({ queryKey: boardsKeys.cardDetail(v.boardId, v.cardId) })

      // Snapshot previous values for rollback
      const prevCards = qc.getQueryData<CardData[]>(boardsKeys.cards(v.boardId))
      const prevArchivedCards = qc.getQueryData<CardData[]>(boardsKeys.archivedCards(v.boardId))
      const prevDetail = qc.getQueryData<CardData>(boardsKeys.cardDetail(v.boardId, v.cardId))
      const prevMembers = qc.getQueryData<BoardMemberData[]>(boardsKeys.members(v.boardId))

      const { optimisticAssignees, optimisticLabels } = v
      const safePayload = { ...v.payload }
      delete safePayload.assignees
      delete safePayload.labels
      const displayPayload = {
        ...safePayload,
        ...(optimisticAssignees ? { assignees: optimisticAssignees } : {}),
        ...(optimisticLabels ? { labels: optimisticLabels } : {}),
      }
      if (v.payload.isArchived === true) {
        const target = prevCards?.find((card) => card._id === v.cardId)
        qc.setQueryData<CardData[]>(boardsKeys.cards(v.boardId), (current) => current?.filter((card) => card._id !== v.cardId))
        if (target) qc.setQueryData<CardData[]>(boardsKeys.archivedCards(v.boardId), (current) => [
          ...(current ?? []), { ...target, ...displayPayload, isArchived: true } as CardData,
        ])
      } else if (v.payload.isArchived === false) {
        const target = prevArchivedCards?.find((card) => card._id === v.cardId)
        qc.setQueryData<CardData[]>(boardsKeys.archivedCards(v.boardId), (current) => current?.filter((card) => card._id !== v.cardId))
        if (target) qc.setQueryData<CardData[]>(boardsKeys.cards(v.boardId), (current) => [
          ...(current ?? []), { ...target, ...displayPayload, isArchived: false } as CardData,
        ])
      } else {
        qc.setQueryData<CardData[]>(boardsKeys.cards(v.boardId), (old) =>
          old?.map((card) => card._id === v.cardId ? { ...card, ...displayPayload } as CardData : card),
        )
      }

      // Optimistically update the card detail cache
      qc.setQueryData<CardData>(boardsKeys.cardDetail(v.boardId, v.cardId), (old) =>
        old ? { ...old, ...displayPayload } as CardData : old
      )

      if (optimisticAssignees) {
        qc.setQueryData<BoardMemberData[]>(boardsKeys.members(v.boardId), (current) => {
          const existingUserIds = new Set((current ?? []).map((member) => member.userId._id))
          const addedMembers = optimisticAssignees
            .filter((user) => !existingUserIds.has(user._id))
            .map((user) => ({ _id: `optimistic-member-${user._id}`, boardId: v.boardId, userId: user, role: 'member' as const }))
          return [...(current ?? []), ...addedMembers]
        })
      }

      return { prevCards, prevArchivedCards, prevDetail, prevMembers }
    },
    onError: (e, v, context) => {
      // Rollback to previous cache on failure
      qc.setQueryData(boardsKeys.cards(v.boardId), context?.prevCards)
      qc.setQueryData(boardsKeys.archivedCards(v.boardId), context?.prevArchivedCards)
      qc.setQueryData(boardsKeys.cardDetail(v.boardId, v.cardId), context?.prevDetail)
      qc.setQueryData(boardsKeys.members(v.boardId), context?.prevMembers)
      toast.error(getErrorMessage(e))
    },
    onSuccess: (updatedCard, v) => {
      qc.setQueryData<CardData[]>(boardsKeys.cards(v.boardId), (current) => current?.map((card) =>
        card._id === v.cardId ? mergeCardServerResponse(card, updatedCard) : card,
      ))
      qc.setQueryData<CardData[]>(boardsKeys.archivedCards(v.boardId), (current) => current?.map((card) =>
        card._id === v.cardId ? mergeCardServerResponse(card, updatedCard) : card,
      ))
      qc.setQueryData<CardData>(boardsKeys.cardDetail(v.boardId, v.cardId), (current) =>
        mergeCardServerResponse(current, updatedCard),
      )
      qc.refetchQueries({ queryKey: boardsKeys.activities(v.boardId, v.cardId), type: 'active' })
      if (v.payload.isArchived !== undefined) qc.invalidateQueries({ queryKey: boardsKeys.archivedCards(v.boardId) })
    },
  })
}

/**
 * Optimistic update: instantly moves the card to the target list in the
 * cache. The card disappears from the source list and appears at the
 * bottom of the target list before the API responds.
 */
export function useMoveCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, cardId, payload }: { boardId: string; cardId: string; payload: MoveCardPayload }) =>
      moveCard(boardId, cardId, payload),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: boardsKeys.cards(v.boardId) })

      const prevCards = qc.getQueryData<CardData[]>(boardsKeys.cards(v.boardId))

      // Optimistically move the card to the new list with the new position
      qc.setQueryData<CardData[]>(boardsKeys.cards(v.boardId), (old) =>
        old?.map((card) =>
          card._id === v.cardId
            ? { ...card, listId: v.payload.listId, position: v.payload.position }
            : card
        )
      )

      return { prevCards }
    },
    onError: (e, v, context) => {
      if (context?.prevCards) {
        qc.setQueryData(boardsKeys.cards(v.boardId), context.prevCards)
      }
      toast.error(getErrorMessage(e))
    },
    onSuccess: (updatedCard, v) => {
      // The position is already shown immediately. Confirm only the fields
      // relevant to movement instead of refetching and causing a visible jump.
      qc.setQueryData<CardData[]>(boardsKeys.cards(v.boardId), (current) => current?.map((card) =>
        card._id === v.cardId
          ? { ...card, listId: updatedCard.listId, position: updatedCard.position }
          : card,
      ))
      // Moving a card creates a server-side activity entry. Refresh the active
      // activity query immediately so the timeline does not wait for a page
      // reload (or the 30s query stale time) to show it.
      qc.refetchQueries({ queryKey: boardsKeys.activities(v.boardId, v.cardId), type: 'active' })
    },
  })
}

/**
 * Optimistic update: instantly removes the card from the cache.
 */
export function useDeleteCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, cardId }: { boardId: string; cardId: string }) =>
      deleteCard(boardId, cardId),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: boardsKeys.cards(v.boardId) })
      await qc.cancelQueries({ queryKey: boardsKeys.archivedCards(v.boardId) })

      const prevCards = qc.getQueryData<CardData[]>(boardsKeys.cards(v.boardId))
      const prevArchivedCards = qc.getQueryData<CardData[]>(boardsKeys.archivedCards(v.boardId))
      const target = prevCards?.find((card) => card._id === v.cardId)

      // Optimistically remove the card
      qc.setQueryData<CardData[]>(boardsKeys.cards(v.boardId), (old) =>
        old?.filter((card) => card._id !== v.cardId)
      )
      if (target) qc.setQueryData<CardData[]>(boardsKeys.archivedCards(v.boardId), (current) => [
        ...(current ?? []), { ...target, isArchived: true },
      ])

      return { prevCards, prevArchivedCards }
    },
    onError: (e, v, context) => {
      qc.setQueryData(boardsKeys.cards(v.boardId), context?.prevCards)
      qc.setQueryData(boardsKeys.archivedCards(v.boardId), context?.prevArchivedCards)
      toast.error(getErrorMessage(e))
    },
    onSuccess: (_result, v) => {
      qc.refetchQueries({ queryKey: boardsKeys.activities(v.boardId, v.cardId), type: 'active' })
    },
  })
}

export function useUploadCardAttachments() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, cardId, files }: { boardId: string; cardId: string; files: File[] }) => uploadCardAttachments(boardId, cardId, files),
    onMutate: async (variables) => {
      const cardsKey = boardsKeys.cards(variables.boardId)
      const detailKey = boardsKeys.cardDetail(variables.boardId, variables.cardId)
      await Promise.all([qc.cancelQueries({ queryKey: cardsKey }), qc.cancelQueries({ queryKey: detailKey })])
      const previousCards = qc.getQueryData<CardData[]>(cardsKey)
      const previousDetail = qc.getQueryData<CardData>(detailKey)
      const createdAt = new Date().toISOString()
      const temporaryAttachments: CardAttachmentData[] = variables.files.map((file) => ({
        _id: optimisticId('attachment'), url: URL.createObjectURL(file), name: file.name,
        mimeType: file.type || 'application/octet-stream', size: file.size, createdAt,
      }))
      const patchCard = (card: CardData) => ({ ...card, attachments: [...(card.attachments ?? []), ...temporaryAttachments] })
      qc.setQueryData<CardData[]>(cardsKey, (current) => current?.map((card) => card._id === variables.cardId ? patchCard(card) : card))
      qc.setQueryData<CardData>(detailKey, (current) => current ? patchCard(current) : current)
      return { previousCards, previousDetail, temporaryAttachments }
    },
    onSuccess: (updatedCard, variables, context) => {
      context?.temporaryAttachments.forEach((attachment) => URL.revokeObjectURL(attachment.url))
      qc.setQueryData<CardData[]>(boardsKeys.cards(variables.boardId), (current) => current?.map((card) =>
        card._id === variables.cardId ? mergeCardServerResponse(card, updatedCard) : card,
      ))
      qc.setQueryData<CardData>(boardsKeys.cardDetail(variables.boardId, variables.cardId), (current) =>
        mergeCardServerResponse(current, updatedCard),
      )
      qc.refetchQueries({ queryKey: boardsKeys.activities(variables.boardId, variables.cardId), type: 'active' })
    },
    onError: (error, variables, context) => {
      context?.temporaryAttachments.forEach((attachment) => URL.revokeObjectURL(attachment.url))
      qc.setQueryData(boardsKeys.cards(variables.boardId), context?.previousCards)
      qc.setQueryData(boardsKeys.cardDetail(variables.boardId, variables.cardId), context?.previousDetail)
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDeleteCardAttachment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, cardId, attachmentId }: { boardId: string; cardId: string; attachmentId: string }) => deleteCardAttachment(boardId, cardId, attachmentId),
    onMutate: async (variables) => {
      const cardsKey = boardsKeys.cards(variables.boardId)
      const detailKey = boardsKeys.cardDetail(variables.boardId, variables.cardId)
      await Promise.all([qc.cancelQueries({ queryKey: cardsKey }), qc.cancelQueries({ queryKey: detailKey })])
      const previousCards = qc.getQueryData<CardData[]>(cardsKey)
      const previousDetail = qc.getQueryData<CardData>(detailKey)
      const patchCard = (card: CardData) => ({ ...card, attachments: card.attachments?.filter((attachment) => attachment._id !== variables.attachmentId) ?? [] })
      qc.setQueryData<CardData[]>(cardsKey, (current) => current?.map((card) => card._id === variables.cardId ? patchCard(card) : card))
      qc.setQueryData<CardData>(detailKey, (current) => current ? patchCard(current) : current)
      return { previousCards, previousDetail }
    },
    onError: (error, variables, context) => {
      qc.setQueryData(boardsKeys.cards(variables.boardId), context?.previousCards)
      qc.setQueryData(boardsKeys.cardDetail(variables.boardId, variables.cardId), context?.previousDetail)
      toast.error(getErrorMessage(error))
    },
    onSuccess: (_result, variables) => {
      qc.refetchQueries({ queryKey: boardsKeys.activities(variables.boardId, variables.cardId), type: 'active' })
    },
  })
}

// ─── Comments ──────────────────────────────────────

export function useCardComments(boardId: string | null, cardId: string | null) {
  return useQuery({
    queryKey: boardsKeys.comments(boardId ?? '', cardId ?? ''),
    queryFn: () => getCardComments(boardId!, cardId!),
    enabled: !!boardId && !!cardId,
    staleTime: 30 * 1000,
  })
}

export function useAddComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, cardId, content, mentions }: { boardId: string; cardId: string; content: string; mentions?: string[] }) =>
      addCardComment(boardId, cardId, { content, mentions }),
    onMutate: async (v) => {
      const commentsKey = boardsKeys.comments(v.boardId, v.cardId)
      await qc.cancelQueries({ queryKey: commentsKey })
      const previousComments = qc.getQueryData<CommentData[]>(commentsKey)
      const temporaryComment: CommentData = {
        _id: optimisticId('comment'), cardId: v.cardId,
        authorId: { _id: 'current-user', firstName: 'You', lastName: '', email: '' },
        content: v.content, mentions: v.mentions ?? [], isEdited: false, createdAt: new Date().toISOString(),
      }
      qc.setQueryData<CommentData[]>(commentsKey, (current) => [...(current ?? []), temporaryComment])
      qc.setQueryData<CardData[]>(boardsKeys.cards(v.boardId), (current) => current?.map((card) =>
        card._id === v.cardId ? { ...card, commentsCount: (card.commentsCount ?? 0) + 1 } : card,
      ))
      return { previousComments, temporaryCommentId: temporaryComment._id }
    },
    onSuccess: (comment, v, context) => {
      qc.setQueryData<CommentData[]>(boardsKeys.comments(v.boardId, v.cardId), (current) =>
        current?.map((entry) => entry._id === context?.temporaryCommentId ? comment : entry),
      )
      qc.refetchQueries({ queryKey: boardsKeys.activities(v.boardId, v.cardId), type: 'active' })
    },
    onError: (e, v, context) => {
      qc.setQueryData(boardsKeys.comments(v.boardId, v.cardId), context?.previousComments)
      qc.setQueryData<CardData[]>(boardsKeys.cards(v.boardId), (current) => current?.map((card) =>
        card._id === v.cardId ? { ...card, commentsCount: Math.max(0, (card.commentsCount ?? 1) - 1) } : card,
      ))
      toast.error(getErrorMessage(e))
    },
  })
}

// ─── Checklists ────────────────────────────────────

export function useCardChecklists(boardId: string | null, cardId: string | null) {
  return useQuery({
    queryKey: boardsKeys.checklists(boardId ?? '', cardId ?? ''),
    queryFn: () => getCardChecklists(boardId!, cardId!),
    enabled: !!boardId && !!cardId,
    staleTime: 30 * 1000,
  })
}

export function useCreateChecklist() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, cardId, title }: { boardId: string; cardId: string; title: string }) =>
      createChecklist(boardId, cardId, { title }),
    onMutate: async (v) => {
      const queryKey = boardsKeys.checklists(v.boardId, v.cardId)
      await qc.cancelQueries({ queryKey })
      await Promise.all([
        qc.cancelQueries({ queryKey: boardsKeys.cards(v.boardId) }),
        qc.cancelQueries({ queryKey: boardsKeys.cardDetail(v.boardId, v.cardId) }),
      ])
      const previousChecklists = qc.getQueryData<ChecklistData[]>(queryKey)
      const previousCards = qc.getQueryData<CardData[]>(boardsKeys.cards(v.boardId))
      const previousDetail = qc.getQueryData<CardData>(boardsKeys.cardDetail(v.boardId, v.cardId))
      const temporaryChecklist: ChecklistData = {
        _id: optimisticId('checklist'), cardId: v.cardId, title: v.title,
        position: (previousChecklists?.length ?? 0) + 1, items: [],
      }
      qc.setQueryData<ChecklistData[]>(queryKey, (current) => [...(current ?? []), temporaryChecklist])
      qc.setQueryData<CardData[]>(boardsKeys.cards(v.boardId), (current) => current?.map((card) =>
        card._id === v.cardId ? { ...card, checklistTotal: (card.checklistTotal ?? 0) + 1 } : card,
      ))
      qc.setQueryData<CardData>(boardsKeys.cardDetail(v.boardId, v.cardId), (current) => current && { ...current, checklistTotal: (current.checklistTotal ?? 0) + 1 })
      return { previousChecklists, previousCards, previousDetail, temporaryChecklistId: temporaryChecklist._id }
    },
    onSuccess: (checklist, v, context) => {
      const resolvedChecklist: ChecklistData = { ...checklist, items: checklist.items ?? [] }
      qc.setQueryData<ChecklistData[]>(boardsKeys.checklists(v.boardId, v.cardId), (current) =>
        current?.map((entry) => entry._id === context?.temporaryChecklistId ? resolvedChecklist : entry),
      )
      qc.refetchQueries({ queryKey: boardsKeys.activities(v.boardId, v.cardId), type: 'active' })
    },
    onError: (e, v, context) => {
      qc.setQueryData(boardsKeys.checklists(v.boardId, v.cardId), context?.previousChecklists)
      qc.setQueryData(boardsKeys.cards(v.boardId), context?.previousCards)
      qc.setQueryData(boardsKeys.cardDetail(v.boardId, v.cardId), context?.previousDetail)
      toast.error(getErrorMessage(e))
    },
  })
}

export function useDeleteChecklist() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, cardId, checklistId }: { boardId: string; cardId: string; checklistId: string }) =>
      deleteChecklist(boardId, cardId, checklistId),
    onMutate: async (v) => {
      const checklistsKey = boardsKeys.checklists(v.boardId, v.cardId)
      await qc.cancelQueries({ queryKey: checklistsKey })
      const previousChecklists = qc.getQueryData<ChecklistData[]>(checklistsKey)
      const previousCards = qc.getQueryData<CardData[]>(boardsKeys.cards(v.boardId))
      const previousDetail = qc.getQueryData<CardData>(boardsKeys.cardDetail(v.boardId, v.cardId))
      const checklist = previousChecklists?.find((entry) => entry._id === v.checklistId)
      const items = checklist?.items ?? []
      const removedTotal = items.length
      const removedCompleted = items.filter((item) => item.isCompleted).length

      qc.setQueryData<ChecklistData[]>(checklistsKey, (current) => current?.filter((entry) => entry._id !== v.checklistId))
      const updateSummary = (card: CardData) => ({
        ...card,
        checklistTotal: Math.max(0, (card.checklistTotal ?? 0) - removedTotal),
        checklistCompleted: Math.max(0, (card.checklistCompleted ?? 0) - removedCompleted),
      })
      qc.setQueryData<CardData[]>(boardsKeys.cards(v.boardId), (current) => current?.map((card) => card._id === v.cardId ? updateSummary(card) : card))
      qc.setQueryData<CardData>(boardsKeys.cardDetail(v.boardId, v.cardId), (current) => current ? updateSummary(current) : current)

      return { previousChecklists, previousCards, previousDetail }
    },
    onError: (error, v, context) => {
      qc.setQueryData(boardsKeys.checklists(v.boardId, v.cardId), context?.previousChecklists)
      qc.setQueryData(boardsKeys.cards(v.boardId), context?.previousCards)
      qc.setQueryData(boardsKeys.cardDetail(v.boardId, v.cardId), context?.previousDetail)
      toast.error(getErrorMessage(error))
    },
    onSuccess: (_result, v) => {
      qc.refetchQueries({ queryKey: boardsKeys.activities(v.boardId, v.cardId), type: 'active' })
    },
  })
}

/**
 * Applies a change to one checklist's items in the cache and shifts the card's
 * checklist counts by the change in leaf items, so sub-item rules (derived
 * parents, cascades) keep the card-face badge in step with the backend.
 */
function applyChecklistChange(
  qc: ReturnType<typeof useQueryClient>,
  boardId: string,
  cardId: string,
  checklistId: string,
  change: (items: ChecklistItemData[]) => ChecklistItemData[],
) {
  const queryKey = boardsKeys.checklists(boardId, cardId)
  const before = countChecklistLeaves(
    qc.getQueryData<ChecklistData[]>(queryKey)?.find((checklist) => checklist._id === checklistId)?.items ?? [],
  )
  let after = before
  qc.setQueryData<ChecklistData[]>(queryKey, (current) => current?.map((checklist) => {
    if (checklist._id !== checklistId) return checklist
    const items = change(checklist.items ?? [])
    after = countChecklistLeaves(items)
    return { ...checklist, items }
  }))
  const totalDelta = after.total - before.total
  const completedDelta = after.completed - before.completed
  if (!totalDelta && !completedDelta) return
  const shift = (card: CardData): CardData => ({
    ...card,
    checklistTotal: Math.max(0, (card.checklistTotal ?? 0) + totalDelta),
    checklistCompleted: Math.max(0, (card.checklistCompleted ?? 0) + completedDelta),
  })
  qc.setQueryData<CardData[]>(boardsKeys.cards(boardId), (current) => current?.map((card) => (card._id === cardId ? shift(card) : card)))
  qc.setQueryData<CardData>(boardsKeys.cardDetail(boardId, cardId), (current) => current && shift(current))
}

export function useCreateChecklistItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, cardId, checklistId, title, parentItemId }: { boardId: string; cardId: string; checklistId: string; title: string; parentItemId?: string }) =>
      createChecklistItem(boardId, cardId, checklistId, { title, ...(parentItemId && { parentItemId }) }),
    onMutate: async (v) => {
      const queryKey = boardsKeys.checklists(v.boardId, v.cardId)
      await qc.cancelQueries({ queryKey })
      await Promise.all([
        qc.cancelQueries({ queryKey: boardsKeys.cards(v.boardId) }),
        qc.cancelQueries({ queryKey: boardsKeys.cardDetail(v.boardId, v.cardId) }),
      ])
      const previousChecklists = qc.getQueryData<ChecklistData[]>(queryKey)
      const previousCards = qc.getQueryData<CardData[]>(boardsKeys.cards(v.boardId))
      const previousDetail = qc.getQueryData<CardData>(boardsKeys.cardDetail(v.boardId, v.cardId))
      const siblings = (previousChecklists?.find((checklist) => checklist._id === v.checklistId)?.items ?? [])
        .filter((item) => (item.parentItemId ?? null) === (v.parentItemId ?? null))
      const temporaryItem: ChecklistItemData = {
        _id: optimisticId('checklist-item'), checklistId: v.checklistId, title: v.title,
        isCompleted: false,
        parentItemId: v.parentItemId ?? null,
        position: Math.max(0, ...siblings.map((item) => item.position)) + 1000,
      }
      applyChecklistChange(qc, v.boardId, v.cardId, v.checklistId, (items) => addChecklistItem(items, temporaryItem))
      return { previousChecklists, previousCards, previousDetail, temporaryItemId: temporaryItem._id }
    },
    onSuccess: (item, v, context) => {
      qc.setQueryData<ChecklistData[]>(boardsKeys.checklists(v.boardId, v.cardId), (current) => current?.map((checklist) =>
        checklist._id === v.checklistId
          ? { ...checklist, items: (checklist.items ?? []).map((entry) => entry._id === context?.temporaryItemId ? item : entry) }
          : checklist,
      ))
      qc.refetchQueries({ queryKey: boardsKeys.activities(v.boardId, v.cardId), type: 'active' })
    },
    onError: (e, v, context) => {
      qc.setQueryData(boardsKeys.checklists(v.boardId, v.cardId), context?.previousChecklists)
      qc.setQueryData(boardsKeys.cards(v.boardId), context?.previousCards)
      qc.setQueryData(boardsKeys.cardDetail(v.boardId, v.cardId), context?.previousDetail)
      toast.error(getErrorMessage(e))
    },
  })
}

/**
 * Optimistic update: instantly toggles the checklist item's completion
 * state in the cache so the checkbox feels instant.
 */
export function useUpdateChecklistItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, cardId, checklistId, itemId, payload }: { boardId: string; cardId: string; checklistId: string; itemId: string; payload: { isCompleted?: boolean; title?: string } }) =>
      updateChecklistItem(boardId, cardId, checklistId, itemId, payload),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: boardsKeys.checklists(v.boardId, v.cardId) })

      const prevChecklists = qc.getQueryData<ChecklistData[]>(boardsKeys.checklists(v.boardId, v.cardId))
      const prevCards = qc.getQueryData<CardData[]>(boardsKeys.cards(v.boardId))
      const prevDetail = qc.getQueryData<CardData>(boardsKeys.cardDetail(v.boardId, v.cardId))

      // Optimistically update the item; ticking cascades to sub-items and
      // re-derives the parent, exactly as the backend does.
      applyChecklistChange(qc, v.boardId, v.cardId, v.checklistId, (items) => {
        const { isCompleted, ...rest } = v.payload
        const merged = items.map((item) => (item._id === v.itemId ? { ...item, ...rest } : item))
        return isCompleted === undefined ? merged : toggleChecklistItem(merged, v.itemId, isCompleted)
      })

      return { prevChecklists, prevCards, prevDetail }
    },
    onError: (e, v, context) => {
      if (context?.prevChecklists) {
        qc.setQueryData(boardsKeys.checklists(v.boardId, v.cardId), context.prevChecklists)
      }
      qc.setQueryData(boardsKeys.cards(v.boardId), context?.prevCards)
      qc.setQueryData(boardsKeys.cardDetail(v.boardId, v.cardId), context?.prevDetail)
      toast.error(getErrorMessage(e))
    },
    onSuccess: (_item, v) => {
      qc.refetchQueries({ queryKey: boardsKeys.activities(v.boardId, v.cardId), type: 'active' })
    },
  })
}

/**
 * Optimistic update: instantly removes the checklist item from the cache.
 */
export function useDeleteChecklistItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, cardId, checklistId, itemId }: { boardId: string; cardId: string; checklistId: string; itemId: string }) =>
      deleteChecklistItem(boardId, cardId, checklistId, itemId),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: boardsKeys.checklists(v.boardId, v.cardId) })

      const prevChecklists = qc.getQueryData<ChecklistData[]>(boardsKeys.checklists(v.boardId, v.cardId))
      const prevCards = qc.getQueryData<CardData[]>(boardsKeys.cards(v.boardId))
      const prevDetail = qc.getQueryData<CardData>(boardsKeys.cardDetail(v.boardId, v.cardId))

      // Optimistically remove the item (and its sub-items, if it is a parent)
      applyChecklistChange(qc, v.boardId, v.cardId, v.checklistId, (items) => removeChecklistItem(items, v.itemId))

      return { prevChecklists, prevCards, prevDetail }
    },
    onError: (e, v, context) => {
      if (context?.prevChecklists) {
        qc.setQueryData(boardsKeys.checklists(v.boardId, v.cardId), context.prevChecklists)
      }
      qc.setQueryData(boardsKeys.cards(v.boardId), context?.prevCards)
      qc.setQueryData(boardsKeys.cardDetail(v.boardId, v.cardId), context?.prevDetail)
      toast.error(getErrorMessage(e))
    },
    onSuccess: (_result, v) => {
      qc.refetchQueries({ queryKey: boardsKeys.activities(v.boardId, v.cardId), type: 'active' })
    },
  })
}

// ─── Activities ────────────────────────────────────

export function useCardActivities(boardId: string | null, cardId: string | null) {
  return useQuery({
    queryKey: boardsKeys.activities(boardId ?? '', cardId ?? ''),
    queryFn: () => getCardActivities(boardId!, cardId!),
    enabled: !!boardId && !!cardId,
    // Activity is an audit trail; showing an old timeline is more confusing
    // than the small request needed when the card is opened again.
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

// ─── Labels ────────────────────────────────────────

export function useBoardLabels(boardId: string | null) {
  return useQuery({
    queryKey: boardsKeys.labels(boardId ?? ''),
    queryFn: () => getBoardLabels(boardId!),
    enabled: !!boardId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, name, color }: { boardId: string; name: string; color?: string }) =>
      createBoardLabel(boardId, { name, color }),
    onMutate: async ({ boardId, name, color }) => {
      const queryKey = boardsKeys.labels(boardId)
      await qc.cancelQueries({ queryKey })
      const previousLabels = qc.getQueryData<LabelData[]>(queryKey)
      const temporaryLabel: LabelData = {
        _id: optimisticId('label'), boardId, name, color: color ?? '#64748b',
      }
      qc.setQueryData<LabelData[]>(queryKey, (current) => [...(current ?? []), temporaryLabel])
      return { previousLabels, temporaryLabelId: temporaryLabel._id }
    },
    onSuccess: (label, v, context) => {
      qc.setQueryData<LabelData[]>(boardsKeys.labels(v.boardId), (current) => current?.map((entry) =>
        entry._id === context?.temporaryLabelId ? label : entry,
      ))
      toast.success('Label created')
    },
    onError: (e, v, context) => {
      qc.setQueryData(boardsKeys.labels(v.boardId), context?.previousLabels)
      toast.error(getErrorMessage(e))
    },
  })
}

export function useUpdateLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, labelId, name, color }: { boardId: string; labelId: string; name: string; color?: string }) =>
      updateBoardLabel(boardId, labelId, { name, color }),
    onMutate: async ({ boardId, labelId, name, color }) => {
      const queryKey = boardsKeys.labels(boardId)
      await qc.cancelQueries({ queryKey })
      const previousLabels = qc.getQueryData<LabelData[]>(queryKey)
      qc.setQueryData<LabelData[]>(queryKey, (current) => (current ?? []).map((label) =>
        label._id === labelId ? { ...label, name, ...(color ? { color } : {}) } : label,
      ))
      return { previousLabels }
    },
    onSuccess: (label, variables) => {
      qc.setQueryData<LabelData[]>(boardsKeys.labels(variables.boardId), (current) =>
        current?.map((entry) => entry._id === label._id ? label : entry),
      )
      void qc.invalidateQueries({ queryKey: boardsKeys.cards(variables.boardId) })
    },
    onError: (error, variables, context) => {
      qc.setQueryData(boardsKeys.labels(variables.boardId), context?.previousLabels)
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDeleteLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, labelId }: { boardId: string; labelId: string }) =>
      deleteBoardLabel(boardId, labelId),
    onMutate: async ({ boardId, labelId }) => {
      const queryKey = boardsKeys.labels(boardId)
      await qc.cancelQueries({ queryKey })
      const previousLabels = qc.getQueryData<LabelData[]>(queryKey)
      qc.setQueryData<LabelData[]>(queryKey, (current) => (current ?? []).filter((label) => label._id !== labelId))
      return { previousLabels }
    },
    onSuccess: (_result, variables) => {
      void qc.invalidateQueries({ queryKey: boardsKeys.cards(variables.boardId) })
    },
    onError: (error, variables, context) => {
      qc.setQueryData(boardsKeys.labels(variables.boardId), context?.previousLabels)
      toast.error(getErrorMessage(error))
    },
  })
}
